import { useState } from "react";
import {
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Link, useNavigate } from "react-router-dom";
import { rankItem } from "@tanstack/match-sorter-utils";

type UserDemoTableUiProps = {
  data: any;
  columns: any;
  pagination?: any;
  noDataMessage?: string | undefined;
  hideSearchInput?: boolean;
  tableTitle?: string;
  additionalButton?: string | null;
  btnUrl?: string;
  customLayout?: boolean;
  hasMore?: any;
  onLoadMore: () => void;
  isLoading?: boolean;
};

// type SearchInputProps = {
//   value?: string | undefined;
//   onChange: (value: string) => void;
//   debounce?: number;
//   className?: string;
//   placeholder?: string;
// };

const fuzzyFilter: FilterFn<any> = (
  row: { getValue: (columnId: string) => any },
  columnId: string,
  value: any,
  addMeta: (meta: { itemRank: { passed: boolean } }) => void
) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

function UserDemoTableUi({
  data,
  columns,
  // pagination,
  noDataMessage,
  hideSearchInput,
  tableTitle,
  additionalButton,
  btnUrl,
  customLayout,
  hasMore,
  onLoadMore,
  isLoading,
}: UserDemoTableUiProps) {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // const filteredData = useMemo(() => {
  //   return filterDataByDate(data, selectedValue);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [data, selectedValue]);

  const table = useReactTable({
    data: data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  function handleRedirect() {
    navigate("/sessions");
  }

  return (
    <div
      className={`flex flex-col overflow-x-auto ${
        tableTitle ? "gap-3" : ""
      }  w-full bg-white  `}
    >
      <div
        className={` flex md:flex-row flex-col md:items-center  justify-between mx-3 md:mx-5 ${
          tableTitle ? "mt-10 gap-3" : ""
        }`}
      >
        <h3 className=" w-full font-semibold text-3xl page-heading text-gray-800">
          {tableTitle || null}
        </h3>

        {/* <div className="w-full">
          <DateFilterForm
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            customRange={customRange}
            setCustomRange={setCustomRange}
          />
        </div> */}

        <div className=" w-full flex justify-end items-center gap-3">
          {/* {!hideSearchInput && (
            <div className="border rounded-md flex items-center gap-3 border-gray-300 h-9 px-3 w-full text-sm focus:outline-none">
              <SearchInput
                value={globalFilter || ""}
                onChange={(value: string) => setGlobalFilter(String(value))}
                className="  flex-1  "
                placeholder="Search"
              />
              <FaSearch className="w-4 h-4 text-gray-400" />
            </div>
          )} */}

          {btnUrl && additionalButton ? (
            <div className=" md:flex  items-center gap-3 ">
              <Link to={btnUrl}>
                <div className="  bg-black text-white  text-sm font-semibold tracking-wide rounded focus:outline-none h-9 flex justify-center items-center w-20 ">
                  {additionalButton}
                </div>
              </Link>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-300">
        <h3 className="text-xl font-semibold text-gray-800">
          {tableTitle || "Last Active Sessions"}
        </h3>
        <div className="flex gap-3">
          <button className="flex items-center gap-2 px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100">
            <img
              src="/icons/user_demo/sort_filter.svg"
              alt="down"
              className="h-6 w-6"
            />
            <span>Sort</span>
            <img
              src="/icons/user_demo/chevron-down-filter.svg"
              alt="down"
              className="h-6 w-6"
            />
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100">
            <img
              src="/icons/user_demo/E_C_Filter.svg"
              alt="down"
              className="h-6 w-6"
            />
            <span className="text-base">Filter</span>
            <img
              src="/icons/user_demo/chevron-down-filter.svg"
              alt="down"
              className="h-6 w-6"
            />
          </button>
        </div>
      </div>
      <div className="overflow-y-auto max-h-[400px]">
        <table className={`mx-auto ${tableTitle ? "mt-5" : ""}  w-full`}>
          <thead className=" h-11 bg-[#F0F4F8] w-full py-1">
            {table.getHeaderGroups().map((headerGroup: any) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header: any, index: number) => {
                  const obj = columns[index];
                  const columnClass = obj.columnClass || "";
                  return (
                    <th
                      key={header.id}
                      className={` whitespace-pre-wrap  max-w-xs md:pl-5 pl-3  text-left font-bold text-[#323232] ${columnClass} `}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header?.column?.columnDef?.header,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {/* {data.length > 0 ? ( */}
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <tr key={index} className="animate-pulse bg-white h-16">
                  {columns.map((_: any, colIndex: any) => (
                    <td key={colIndex} className="px-4 py-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-8 text-gray-500 font-medium"
                >
                  {noDataMessage || "No data found"}
                </td>
              </tr>
            ) : (
              table?.getRowModel().rows.map((row: any, index: number) => {
                return (
                  <tr
                    key={row.id}
                    className={` ${
                      index % 2 !== 0 ? "bg-bg-white" : "bg-white"
                    }   `}
                  >
                    {row.getVisibleCells().map((cell: any, index: number) => {
                      const obj = columns[index];
                      const columnClass = obj.columnClass || "";

                      return (
                        <td
                          key={cell.id}
                          className={` whitespace-pre-wrap  md:px-5 p-3 text-base text-[#323232] border-b border-gray-300 ${columnClass} `}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* {visibleRows < data.length && (
        <div className="sticky bottom-0 bg-white py-3 shadow-md flex justify-end">
          <button
            onClick={() => setVisibleRows((prev) => prev + 5)}
            className="text-[#4589FF] px-4 py-2 rounded-md flex justify-end text-base font-normal"
          >
            Show more
          </button>
        </div>
      )} */}

      {hasMore && (
        <div className="sticky bottom-0 bg-white py-3 shadow-md flex justify-end">
          <button
            // onClick={onLoadMore}
            onClick={handleRedirect}
            className="text-[#4589FF] px-4 py-2 rounded-md flex justify-end text-base font-normal"
          >
            Show more
            <span>
              <img
                src="/icons/user_demo/chevron-down-show.svg"
                alt="down"
                className="h-6 w-6"
              />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

export default UserDemoTableUi;

// function SearchInput({
//   value: initialValue,
//   onChange,
//   debounce = 500,
//   className,

//   ...props
// }: SearchInputProps) {
//   const [value, setValue] = useState(initialValue || "");
//   useEffect(() => {
//     setValue(initialValue || "");
//   }, [initialValue]);

//   useEffect(() => {
//     const timeout = setTimeout(() => {
//       onChange(value);
//     }, debounce);

//     return () => clearTimeout(timeout);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [value]);

//   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     setValue(e.target.value);
//   };
//   return (
//     <input
//       {...props}
//       value={value}
//       onChange={handleInputChange}
//       className={className}
//     />
//   );
// }

// function Pagination({ table }: any) {
//   const pageCount = table.getPageCount();
//   const pageIndex = table.getState().pagination.pageIndex;

//   const maxPagesToShow = 5;
//   const halfMaxPages = Math.floor(maxPagesToShow / 2);

//   const pageRangeStart = Math.max(0, pageIndex - halfMaxPages);
//   const pageRangeEnd = Math.min(pageCount - 1, pageIndex + halfMaxPages);

//   const pageNumbersToShow = [
//     ...Array(pageRangeEnd - pageRangeStart + 1).keys(),
//   ].map((i) => i + pageRangeStart);

//   const handlePageClick = (pageIndex: number) => {
//     table.setPageIndex(pageIndex);
//   };

//   return (
//     <div className=" md:m-5 m-3 flex md:flex-row flex-col  md:items-center md:justify-between justify-center gap-3">
//       <div className="mx-1 hidden items-center text-sm text-gray-700 md:flex">
//         Showing
//         <span className="mx-1 font-medium">
//           {table.getState().pagination.pageIndex + 1}
//         </span>
//         to
//         <span className="mx-1 font-medium">{table.getPageCount()}</span>
//         of
//         <span className="mx-1 font-medium">
//           {table.getPrePaginationRowModel().rows.length}
//         </span>
//         Results
//       </div>

//       <select
//         className="rounded-md border h-9 w-12  text-sm text-gray-500 focus:outline-none"
//         value={table.getState().pagination.pageSize}
//         onChange={(e) => {
//           table.setPageSize(Number(e.target.value));
//         }}
//       >
//         {[10, 20, 30, 40, 50, 100].map((pageSize) => (
//           <option key={pageSize} value={pageSize}>
//             {pageSize}
//           </option>
//         ))}
//       </select>

//       <nav
//         className=" md:gap-2 gap-1  flex items-center"
//         aria-label="Pagination"
//       >
//         <button
//           onClick={() => table.setPageIndex(0)}
//           disabled={!table.getCanPreviousPage()}
//           className="relative hidden md:inline-flex items-center rounded-md h-9 w-9 justify-center text-gray-400 bg-gray-100 focus:z-20 focus:outline-offset-0"
//         >
//           <span className="sr-only">Previous</span>
//           <span>{"<<"}</span>
//         </button>
//         <button
//           onClick={() => table.previousPage()}
//           disabled={!table.getCanPreviousPage()}
//           className="relative inline-flex items-center justify-center rounded-md h-9 w-9 text-gray-400 bg-gray-100 focus:z-20 focus:outline-offset-0"
//         >
//           <span className="sr-only">Previous</span>
//           <span>{"<"}</span>
//         </button>

//         {pageNumbersToShow.map((number) => {
//           const active = number === pageIndex;
//           return (
//             <button
//               key={number}
//               onClick={() => handlePageClick(number)}
//               disabled={active}
//               className={`relative inline-flex items-center h-9 w-9 justify-center ${
//                 active
//                   ? "z-10  bg-[#3F81FE] text-white "
//                   : " border-none  text-gray-900 bg-gray-100"
//               }  text-sm ring-0 font-semibold rounded-md  `}
//             >
//               {number + 1}
//             </button>
//           );
//         })}

//         <button
//           onClick={() => table.nextPage()}
//           disabled={!table.getCanNextPage()}
//           className="relative inline-flex items-center rounded-md h-9 w-9 justify-center text-gray-400 bg-gray-100 focus:z-20 focus:outline-offset-0"
//         >
//           <span className="sr-only">Next</span>
//           <span>{">"}</span>
//         </button>
//         <button
//           onClick={() => table.setPageIndex(table.getPageCount() - 1)}
//           disabled={!table.getCanNextPage()}
//           className="relative hidden md:inline-flex items-center rounded-md h-9 w-9 justify-center text-gray-400 bg-gray-100 focus:z-20 focus:outline-offset-0"
//         >
//           <span className="sr-only">Next</span>
//           <span>{">>"}</span>
//         </button>
//       </nav>
//     </div>
//   );
// }
